/**
 * ExpectedThisWeekSection — Filters patients where nextExpectedDate falls in the current week.
 *
 * Only includes patients with >28 days of history.
 * Default sort: overdue-first then by expected date ascending.
 * Supports user-configurable sort and persisted collapse state.
 */
import React, { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { setPatientPinPreference } from "../../utils/patientDirectoryService";
import { useAuth } from "../../utils/authContext";
import DashboardSection from "./DashboardSection";
import PatientRow from "./PatientRow";
import { sortDashboardPatients } from "./dashboardSortUtil";

function getWeekBounds() {
    const now = new Date();
    const day = now.getDay(); // 0 = Sunday
    const diffToMonday = day === 0 ? 6 : day - 1;

    const monday = new Date(now);
    monday.setDate(now.getDate() - diffToMonday);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return { monday, sunday };
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MIN_HISTORY_DAYS = 28;
// Dormancy guard: a patient lapsed well past their own cadence is no longer
// "expected" — drop once days-since-last-visit exceeds 2× their average
// cadence, capped at an absolute MAX_DORMANT_DAYS. (Product-tunable — see
// docs/updates/2026-06-14-clinical-day-view-expected-week-window.md.)
const DORMANT_CADENCE_MULTIPLIER = 2;
const MAX_DORMANT_DAYS = 60;
const SECTION_TITLE = "Expected This Week";

const ExpectedThisWeekSection = ({ allPatients, dashboardPrefs }) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { getSectionPrefs, setSectionSort, setSectionCollapsed } = dashboardPrefs;
    const sectionPrefs = getSectionPrefs(SECTION_TITLE);

    const expectedPatients = useMemo(() => {
        const now = new Date();
        const { sunday } = getWeekBounds();

        return allPatients
            .filter((p) => {
                const cf = p.contactFrequency;
                if (!cf?.nextExpectedDate || !cf.averageDaysBetween) return false;
                if (!cf.lastDocumentDate || !cf.firstDocumentDate) return false;

                // Require a real history span (first→last visit), not just
                // time-since-last-visit. The old proxy let dormant patients
                // through and excluded genuinely active short-history ones.
                const historySpanDays = (cf.lastDocumentDate - cf.firstDocumentDate) / MS_PER_DAY;
                if (historySpanDays < MIN_HISTORY_DAYS) return false;

                // Dormancy guard: drop patients lapsed well past their cadence
                // so the "overdue" bucket can't accumulate stale patients forever.
                const daysSinceLast = (now - cf.lastDocumentDate) / MS_PER_DAY;
                const dormantCutoff = Math.min(
                    DORMANT_CADENCE_MULTIPLIER * cf.averageDaysBetween,
                    MAX_DORMANT_DAYS
                );
                if (daysSinceLast > dormantCutoff) return false;

                // Due this week, or overdue but still within the (non-dormant)
                // window guarded above. Future visits beyond Sunday are excluded.
                return cf.nextExpectedDate <= sunday;
            })
            .map((p) => ({
                ...p,
                _expectedDate: p.contactFrequency.nextExpectedDate,
                _isOverdue: p.contactFrequency.nextExpectedDate < now,
            }));
    }, [allPatients]);

    const sortedPatients = useMemo(
        () => sortDashboardPatients(expectedPatients, sectionPrefs.sortField, sectionPrefs.sortDirection),
        [expectedPatients, sectionPrefs.sortField, sectionPrefs.sortDirection]
    );

    const handleNavigate = useCallback((id) => {
        navigate("/patient", { state: { patientId: id, from: "/dashboard" } });
    }, [navigate]);

    const handleRecord = useCallback((id) => {
        navigate("/patient", { state: { patientId: id, pendingAction: "record", from: "/dashboard" } });
    }, [navigate]);

    const handlePinToggle = useCallback(async (patient) => {
        if (patient.isShared || !currentUser?.uid) return;
        try {
            await setPatientPinPreference(currentUser.uid, patient.id, !patient.pin);
        } catch (err) {
            console.error("[ExpectedThisWeekSection] Error toggling pin:", err);
        }
    }, [currentUser?.uid]);

    const handleSort = useCallback((field, direction) => {
        setSectionSort(SECTION_TITLE, field, direction);
    }, [setSectionSort]);

    const handleToggleCollapse = useCallback((collapsed) => {
        setSectionCollapsed(SECTION_TITLE, collapsed);
    }, [setSectionCollapsed]);

    if (expectedPatients.length === 0) return null;

    return (
        <DashboardSection
            title={SECTION_TITLE}
            count={expectedPatients.length}
            collapsed={sectionPrefs.collapsed}
            onToggleCollapse={handleToggleCollapse}
            sortField={sectionPrefs.sortField}
            sortDirection={sectionPrefs.sortDirection}
            onSort={handleSort}
            columnHeader={true}
        >
            {sortedPatients.map((patient) => (
                <PatientRow
                    key={patient.id}
                    patient={patient}
                    onNavigate={handleNavigate}
                    onRecord={handleRecord}
                    showPin={!patient.isShared}
                    onPinToggle={handlePinToggle}
                />
            ))}
        </DashboardSection>
    );
};

export default ExpectedThisWeekSection;
